const join = (...parts) =>
  parts
    .filter((part) => part !== undefined && part !== null && part !== "")
    .map((part) => String(part).replace(/(^\/+|\/+$)/g, ""))
    .filter(Boolean)
    .join("/");

const withSlash = (...parts) => `/${join(...parts)}`;

const paths = {
  landing: "/",
  pmDashboard: "/home",
  dashboard: "/dashboard",
  myTasks: "/my-tasks",
  taskDetails: (taskId) => withSlash("my-tasks", taskId),
  taskStage: (taskId, stage) => withSlash("my-tasks", taskId, String(stage || "").toLowerCase()),
  taskHeading: (taskId) => withSlash("my-tasks", taskId, "heading"),
  taskNavigation: (taskId) => withSlash("my-tasks", taskId, "navigation"),
  taskProgress: (taskId) => withSlash("my-tasks", taskId, "progress"),
  taskCompleted: (taskId) => withSlash("my-tasks", taskId, "completed"),
  taskCancel: (taskId) => withSlash("my-tasks", taskId, "cancel"),
  taskCreate: "/my-tasks/new",
  taskCreateGeneral: "/my-tasks/new/general",
  taskCreateTravo: "/my-tasks/new/travo",
  taskCreateCare: "/my-tasks/new/care",
  taskCreateByType: (requestType = "GENERAL") => {
    const type = String(requestType || "GENERAL").toUpperCase();
    if (type === "TRAVO") return "/my-tasks/new/travo";
    if (type === "CARE") return "/my-tasks/new/care";
    return "/my-tasks/new/general";
  },
  requesterProfileDetails: (requesterId) => withSlash("requesters", requesterId),
  helperProfileDetails: (helperId) => withSlash("helpers", helperId),
  actionables: "/actionables",
  notifications: (category = "ACTIVITY") =>
    withSlash("notifications", String(category || "ACTIVITY").toLowerCase()),
  jobSearch: "/job-search",
  jobOpportunityDetails: (jobOpportunityId) =>
    withSlash("job-opportunities", jobOpportunityId),
  jobOpportunityInterviewSessions: (jobOpportunityId) =>
    withSlash("job-opportunities", jobOpportunityId, "interview-sessions"),
  jobPosts: "/job-posts",
  jobApplications: "/job-applications",
  jobApplicationDetails: (applicationId) =>
    withSlash("job-applications", applicationId),
  talentSearch: "/talent-search",
  interviews: "/interviews",
  invitations: "/invitations",
  newJobPost: "/job-posts/new",
  professionalProfiles: "/professional-profiles",
  professionalProfileCreate: "/professional-profiles/new",
  professionalProfileDetails: (profileId) =>
    withSlash("professional-profiles", profileId),
  professionalProfileEdit: (profileId) =>
    withSlash("professional-profiles", profileId, "edit"),
  backgroundChecks: "/background-checks",
  website: "/website",
  websiteTemplates: "/website/templates",
  websiteBuild: (templateId) => withSlash("website", "build", templateId),
  websiteRegenerate: "/website/regenerate",
  websiteGenerating: (websiteId) => withSlash("website", "generating", websiteId),
  websiteSuccess: (websiteId) => withSlash("website", "success", websiteId),
  websiteDomain: "/website/domain",
  legalPrivacy: "https://oliveeb.com/privacy_policy.html",
  chatSpace: "/chat-space",
  emailMessages: "/email-messages",
  accessControl: "/access-control",
  projects: "/projects",
  users: "/users",
  workspaces: "/workspaces",
  organizations: "/organizations",
  organizationDetails: (organizationId) => withSlash("organizations", organizationId),
  publicOrganizationDetails: (organizationId) => withSlash("public", "organizations", organizationId),
  partners: "/partners",
  partnerCommissions: "/partners/commissions",
  workspaceCreate: () => withSlash("workspaces", "new"),
  workspaceDetails: (workspaceId) =>
    withSlash("workspaces", workspaceId),
  workspaceMemberDetails: (workspaceId, userId) =>
    withSlash("workspaces", workspaceId, "members", userId),
  workspaceInvitationAccept: "/workspaces/invitations/accept",
  workspaceInvitation: (workspaceId, invitationId) =>
    withSlash("workspaces", workspaceId, "invitations", invitationId),
  workspaceSubscriptions: (workspaceId) =>
    withSlash("workspaces", workspaceId, "subscriptions"),
  workspacePaymentMethods: (workspaceId) =>
    withSlash("workspaces", workspaceId, "payment-methods"),
  workspaceOrganization: (workspaceId) =>
    withSlash("workspaces", workspaceId, "organization"),
  workspaceCharges: (workspaceId) =>
    withSlash("workspaces", workspaceId, "charges"),
  workspaceEarnings: (workspaceId) =>
    withSlash("workspaces", workspaceId, "earnings"),
  userCreate: "/users/new",
  userEdit: (userId) => withSlash("users", userId, "edit"),
  backlog: "/backlog",
  iterations: "/iterations",
  tags: "/tags",
  team: "/team",
  reports: "/reports",
  settings: "/settings",
  settingsChangePassword: "/settings/change-password",
  settingsSecurityQuestions: "/settings/security-questions",
  settingsVerifyEmail: "/settings/verify-email",
  settingsVerifyPhone: "/settings/verify-phone",
  settingsAppDetails: "/settings/app-details",
  settingsSupportedLocations: "/settings/supported-locations",
  settingsDeleteAccount: "/settings/delete-account",
  settingsNotifications: "/settings/notifications",
  settingsLanguage: "/settings/language",
  settingsTheme: "/settings/theme",
  settingsDataStorage: "/settings/data-storage",
  feedback: "/feedback",
  help: "/app/help",
  support: "/support",
  profile: "/profile",
  simpleLogin: "/login",
  getStarted: "/get-started",
  simpleSignup: "/auth/signup",
  authLanding: "/login",
  appleSetup: "/auth/apple-setup",
  socialSetup: "/auth/social-setup",
  simpleLogout: "/auth/logout",
  simpleSessionExpired: "/auth/session-expired",
  simpleRecoverRequest: "/auth/recover",
  simpleVerificationCode: "/auth/verify",
  simpleResetPassword: "/auth/reset",
  simpleAccountRecoverySuccess: "/auth/recovered",
  cardLogin: "/login",
  cardLogout: "/auth/logout",
  projectCreate: () => withSlash("projects", "new"),
  projectEdit: (projectId) => withSlash("projects", projectId, "edit"),
  projectDetails: (projectId) => withSlash("projects", projectId),
  projectIterationCreate: (projectId) =>
    withSlash("projects", projectId, "iterations", "new"),
  projectIterationEdit: (projectId, iterationId) =>
    withSlash("projects", projectId, "iterations", iterationId, "edit"),
  projectIterationDetails: (projectId, iterationId) =>
    withSlash("projects", projectId, "iterations", iterationId),
  projectTaskCreate: (projectId, iterationId) =>
    withSlash("projects", projectId, "iterations", iterationId, "tasks", "new"),
  projectTaskEdit: (projectId, iterationId, taskId) =>
    withSlash(
      "projects",
      projectId,
      "iterations",
      iterationId,
      "tasks",
      taskId,
      "edit",
    ),
  projectTaskDetails: (projectId, iterationId, taskId) =>
    withSlash("projects", projectId, "iterations", iterationId, "tasks", taskId),

  qaTestRuns: "/qa-test-runs",
  qaTestRunsFeatures: (appId) => withSlash("qa-test-runs", "apps", appId, "features"),
  qaTestRunsAppHistory: (appId) => withSlash("qa-test-runs", "apps", appId, "history"),
  qaTestRunsFeatureHistory: (appId, featureId) =>
    withSlash("qa-test-runs", "apps", appId, "features", featureId, "history"),
};

export default paths;
